import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { resolveSupportedLang } from '../../i18n'
import { track } from '../../analytics/track'
import { ContactFormButton } from './ContactFormButton'
import { BugReportButton } from './BugReportButton'
import { LanguageSwitcher } from './LanguageSwitcher'
import { SocialLinks } from './SocialLinks'

interface MarketingHeaderProps {
  onSelectCustomer: () => void
  onLoginCustomer: () => void
  onLoginCraftsman: () => void
  /** Where the logo links to. Defaults to "/". LandingPage overrides this with "#top" since it IS
   *  the home page there - the logo just scrolls back up rather than reloading the page. */
  logoHref?: string
}

const SECTION_ANCHORS = [
  // Führt zur eingebetteten Preisrechner-Sektion auf der Startseite (docs/tasks/active/seo/seo_v4.mde Aufgabe
  // 4) - bisher als einzige Homepage-Sektion ohne eigenen Nav-Anker, obwohl sie das stärkste
  // konkrete Conversion-/SEO-Asset ist (kostenloses, sofortiges Ergebnis).
  { id: 'kostenrechner', key: 'landing.nav.priceCalculator' },
  { id: 'vorteile', key: 'landing.nav.advantages' },
  { id: 'ablauf', key: 'landing.nav.howItWorks' },
  { id: 'handwerker', key: 'landing.nav.forCraftsmen' },
  { id: 'faq', key: 'landing.nav.faq' },
] as const

/** The dark sticky top nav shared by the landing page and marketing content (blog) - extracted
 *  from LandingPage so both surfaces look and behave identically. Purely presentational: callers
 *  decide what the CTA/login actions actually do (LandingPage wires them to AppShell's auth-mode
 *  state, blog pages wire them via useMarketingHeaderActions). */
export function MarketingHeader({ onSelectCustomer, onLoginCustomer, onLoginCraftsman, logoHref = '/' }: MarketingHeaderProps) {
  const { t, i18n } = useTranslation()
  const location = useLocation()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const lang = resolveSupportedLang(i18n.resolvedLanguage)
  // The anchor targets (#vorteile etc.) only exist on the landing page itself. On other marketing
  // surfaces (blog, pv-rechner) that reuse this header, link back to the landing page with the
  // hash attached instead - a plain <a> so the browser does a full navigation and jumps straight
  // to the section once it lands, rather than silently no-op'ing via client-side routing.
  const isLandingPage = location.pathname === '/' || location.pathname === `/${lang}`
  const sectionHref = (anchor: string) => (isLandingPage ? `#${anchor}` : `/${lang}#${anchor}`)
  // PV-Rechner is German-only regardless of UI language (see usePvRechnerSeo.ts) - the switcher
  // would otherwise hard-navigate visitors away from the tool to a differently-worded homepage,
  // which is jarring and doesn't actually translate anything (see
  // tasks/suggestion/roofora-task-022-suggestion-03, point 2). The blog and the SEO landing pages
  // (content/landingPages) are NOT included here - both exist in all 3 languages, and
  // LanguageSwitcher jumps to the translated sibling page/post rather than the homepage.
  const isGermanOnlyRoute = location.pathname === '/pv-rechner'

  return (
    <header className="landing-nav">
      <div className="landing-nav-inner">
        <div className="landing-logo-group">
          <a className="landing-logo" href={logoHref}>
            <span className="landing-logo-text">
              Roofora
              <span className="beta-badge">Beta</span>
            </span>
            <span className="muted landing-slogan">{t('landing.slogan')}</span>
          </a>
        </div>
        <nav className="landing-nav-links">
          {SECTION_ANCHORS.map(({ id, key }) => (
            <a key={id} href={sectionHref(id)}>
              {t(key)}
            </a>
          ))}
        </nav>
        {/* Kontakt/Fehler melden stay inline on desktop; below the .landing-nav-burger breakpoint
         *  (see App.css) this whole group is hidden and the same controls reappear in the
         *  .landing-nav-mobile-panel dropdown instead. */}
        <div className="landing-nav-utility landing-nav-utility-desktop">
          <ContactFormButton />
          <BugReportButton />
        </div>
        <SocialLinks className="landing-nav-social-links" linkClassName="landing-nav-social-link" />
        <button
          type="button"
          className="landing-nav-burger"
          aria-label="Menü"
          aria-expanded={mobileMenuOpen}
          onClick={() => setMobileMenuOpen((v) => !v)}
        >
          ☰
        </button>
        <div className="landing-nav-actions">
          <div className="landing-nav-lang-desktop">{!isGermanOnlyRoute && <LanguageSwitcher />}</div>
          <details className="login-menu">
            <summary className="login-menu-trigger">{t('landing.nav.login')}</summary>
            <div className="login-menu-panel">
              <button
                type="button"
                onClick={() => {
                  track('login_clicked', { role: 'customer' })
                  onLoginCustomer()
                }}
              >
                {t('landing.nav.loginAsCustomer')}
              </button>
              <button
                type="button"
                onClick={() => {
                  track('login_clicked', { role: 'craftsman' })
                  onLoginCraftsman()
                }}
              >
                {t('landing.nav.loginAsCraftsman')}
              </button>
            </div>
          </details>
          <button
            className="landing-nav-cta"
            onClick={() => {
              track('cta_clicked', { role: 'customer', location: 'nav' })
              onSelectCustomer()
            }}
          >
            {t('landing.nav.compareOffers')}
          </button>
        </div>
      </div>
      {mobileMenuOpen && (
        <div className="landing-nav-mobile-panel">
          <ContactFormButton />
          <BugReportButton />
          {!isGermanOnlyRoute && <LanguageSwitcher />}
        </div>
      )}
    </header>
  )
}
